import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Stack,
  TextField,
  Button,
  Typography,
  MenuItem,
  Paper,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import AddCustomer from "../config/views/pages/aule/add.config";
import { uploadFileToS3 } from "../../utils/s3Uploader";

const RoomForm = ({ initialData, assigned, onSubmit, onClose }) => {
  const fieldsConfig = AddCustomer.SuperAdmin.fields;

  const [formData, setFormData] = useState(() => {
    const initial = {};
    Object.entries(fieldsConfig).forEach(([_, cfg]) => {
      initial[cfg.fieldName] = initialData?.[cfg.fieldName] ?? "";
    });
    initial.assigned = initialData?.assigned || assigned || "";
    initial.user = initialData?.user || initial.assigned;
    return initial;
  });

  const [sedeOptions, setSedeOptions] = useState([]);

  // 🔹 Carica le sedi dal backend se è un remote_select
  useEffect(() => {
    const fetchSedi = async () => {
      const sedeField = fieldsConfig["Sede"];
      if (sedeField?.type === "remote_select" && sedeField.endpoint) {
        try {
          const res = await fetch(sedeField.endpoint);
          const data = await res.json();
          setSedeOptions(Array.isArray(data) ? data : []);
        } catch (err) {
          console.error("Errore caricamento sedi:", err);
        }
      }
    };
    fetchSedi();
  }, []);

  const handleChange = useCallback((e, fieldName) => {
    setFormData((prev) => ({ ...prev, [fieldName]: e.target.value }));
  }, []);

  const handleCheckboxChange = useCallback((e, fieldName) => {
    setFormData((prev) => ({ ...prev, [fieldName]: e.target.checked }));
  }, []);

  const handleFileChange = useCallback(async (e, fieldName) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const url = await uploadFileToS3(file);
      setFormData((prev) => ({ ...prev, [fieldName]: url }));
    } catch (err) {
      alert("Errore upload file: " + err.message);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const method = initialData ? "PUT" : "POST";
    const endpoint = initialData ? `${AddCustomer.SuperAdmin.url}/${initialData.id}` : AddCustomer.SuperAdmin.url;

    try {
      const res = await fetch(endpoint, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      if (!res.ok) throw new Error("Errore durante il salvataggio");
      onSubmit?.();
      onClose?.();
    } catch (err) {
      alert(err.message);
    }
  };

  return (
    <Paper
      elevation={4}
      sx={{
        p: 4,
        bgcolor: "#fafafa",
        borderRadius: 3,
        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
      }}
    >
      <Typography variant="h6" mb={3} fontWeight="bold">
        {initialData ? "Modifica Aula" : "Crea Nuova Aula"}
      </Typography>

      <Box component="form" onSubmit={handleSubmit}>
        <Stack spacing={3}>
          {Object.entries(fieldsConfig).map(([label, cfg]) => {
            // 🔹 Upload file
            if (cfg.type === "file-upload-s3") {
              return (
                <Box
                  key={cfg.fieldName}
                  display="flex"
                  flexDirection="column"
                  alignItems="flex-start"
                  gap={1}
                >
                  <Button variant="contained" color="secondary" component="label">
                    {label}
                    <input
                      type="file"
                      hidden
                      onChange={(e) => handleFileChange(e, cfg.fieldName)}
                    />
                  </Button>
                  {formData[cfg.fieldName] && (
                    <Typography
                      variant="caption"
                      color="green"
                      sx={{ wordBreak: "break-all" }}
                    >
                      File caricato: {formData[cfg.fieldName].split("/").pop()}
                    </Typography>
                  )}
                </Box>
              );
            }

            // 🔹 Select locale o enum
            if (cfg.type === "select" || cfg.type === "enum") {
              return (
                <TextField
                  key={cfg.fieldName}
                  select
                  label={label}
                  value={formData[cfg.fieldName]}
                  onChange={(e) => handleChange(e, cfg.fieldName)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    "& .MuiInputBase-input": { fontWeight: 500 },
                  }}
                >
                  {cfg.choices?.map((choice) => (
                    <MenuItem key={choice} value={choice}>
                      {choice}
                    </MenuItem>
                  ))}
                </TextField>
              );
            }

            // 🔹 Remote select (Sede)
            if (cfg.type === "remote_select") {
              return (
                <TextField
                  key={cfg.fieldName}
                  select
                  label={label}
                  value={formData[cfg.fieldName]}
                  onChange={(e) => handleChange(e, cfg.fieldName)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    "& .MuiInputBase-input": { fontWeight: 500 },
                  }}
                >
                  {sedeOptions.length === 0 ? (
                    <MenuItem disabled>Caricamento sedi...</MenuItem>
                  ) : (
                    sedeOptions.map((option) => (
                      <MenuItem key={option.id} value={option.id}>
                        {option[cfg.targetName] || option.nome_sede}
                      </MenuItem>
                    ))
                  )}
                </TextField>
              );
            }

            // 🔹 Checkbox (Pratica)
            if (cfg.type === "checkbox") {
              return (
                <FormControlLabel
                  key={cfg.fieldName}
                  control={
                    <Checkbox
                      checked={!!formData[cfg.fieldName]}
                      onChange={(e) => handleCheckboxChange(e, cfg.fieldName)}
                      color="primary"
                    />
                  }
                  label={<Typography fontWeight={500}>{label}</Typography>}
                />
              );
            }

            // 🔹 Campo data
            if (cfg.type === "date") {
              return (
                <TextField
                  key={cfg.fieldName}
                  label={label}
                  type="date"
                  value={formData[cfg.fieldName]}
                  onChange={(e) => handleChange(e, cfg.fieldName)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  sx={{
                    "& .MuiInputBase-input": { fontWeight: 500 },
                  }}
                />
              );
            }

            // 🔹 Text / Number
            return (
              <TextField
                key={cfg.fieldName}
                label={label}
                type={cfg.type === "number" ? "number" : "text"}
                value={formData[cfg.fieldName]}
                onChange={(e) => handleChange(e, cfg.fieldName)}
                inputProps={{ maxLength: cfg.length || undefined }}
                fullWidth
                InputLabelProps={{ shrink: true }}
                sx={{
                  "& .MuiInputBase-input": {
                    fontWeight: 500,
                    color: "#222",
                    backgroundColor: "#fff",
                    borderRadius: 1,
                  },
                }}
              />
            );
          })}
        </Stack>

        <input type="hidden" name="assigned" value={formData.assigned} />
        <input type="hidden" name="user" value={formData.user} />

        <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 2 }}>

          <Button onClick={onClose} variant="outlined" color="secondary">
            Annulla
          </Button>
          <Button type="submit" variant="contained" color="primary">
            Salva
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default RoomForm;
